import {
  RETRY_DEPOSIT_ACCOUNT_PAYMENT,
  updateDepositAccountPaymentStatus,
} from '../../../../../shared/actions/depositAccount';
import { selectAnalyticsOfferStatus } from '../../../../../shared/selectors/analytics';
import { getPaymentStatus } from '../../../../../shared/services/billingPayments';
import { trackRetryPaymentClick } from '../../../../../shared/components/Balance/components/PaymentStatus/tracking';
import { openModal } from '../../../../../shared/utils/modals';
import { listenEvent } from '../../../../../shared/utils';
import { trackPaymentCanceled, trackPaymentSuccess, trackPaymentTimeout } from './tracking';
import { IUserService } from '../userService';
import { IApiService } from '../apiService';
import { Store } from 'redux';
import { IAppState } from '../../../../../shared/redux';
import { IHttpApi } from '@cian/http-api';
import { IPlacingTermsApiService } from '../../../publish-terms/apiFactory';
import { EPaymentStatus, TPaymentStatus } from '../../../../../shared/types/reducers/depositAccount';
import { selectMainPaymentProvider } from '../../../../../shared/selectors/paymentProviders';

export const cardPaymentService = [
  '$rootScope',
  'apiService',
  'userService',
  'placingTermsApi',
  'store',
  'httpApi',
  function (
    $rootScope: angular.IRootScopeService,
    apiService: IApiService,
    userService: IUserService,
    placingTermsApi: IPlacingTermsApiService,
    store: Store<IAppState>,
    httpApi: IHttpApi,
  ) {
    let paymentStatus: TPaymentStatus;
    let checkInterval: number;
    let failTimeout: number;
    let isPaymentProcessed: boolean | undefined;
    let paymentSum: number;

    const state = store.getState();
    const analyticsOfferStatus = selectAnalyticsOfferStatus(state);
    const provider = selectMainPaymentProvider(state)!;

    const clearTimers = () => {
      clearInterval(checkInterval);
      clearTimeout(failTimeout);
    };

    const setPaymentStatus = (status: TPaymentStatus) => {
      paymentStatus = status;

      store.dispatch(updateDepositAccountPaymentStatus(status));
    };

    const updateBalance = async () => {
      const { totalBalance } = await apiService.getUserBalance();

      if (totalBalance !== undefined) {
        const isNotEnoughMoney = totalBalance < placingTermsApi.getTotalPriceAmount();

        userService.setBalance(totalBalance);
        placingTermsApi.setNotEnoughMoney(isNotEnoughMoney);
      }
    };

    const checkPayment = (paymentId: string) => async () => {
      const { status } = await getPaymentStatus({ httpApi, paymentId, provider });

      setPaymentStatus(status);

      if (status === EPaymentStatus.Success) {
        clearTimers();

        updateBalance();

        trackPaymentSuccess({ analyticsOfferStatus, amount: paymentSum });
      } else if (status === EPaymentStatus.Fail) {
        trackPaymentCanceled(analyticsOfferStatus);
      }
    };

    const runCheckPaymentState = (paymentId: string) => {
      if (!isPaymentProcessed) {
        return;
      }

      clearTimers();

      setPaymentStatus(EPaymentStatus.Pending);

      checkInterval = window.setInterval(checkPayment(paymentId), 3000); // 3 секунды

      failTimeout = window.setTimeout(() => {
        clearTimers();

        setPaymentStatus(EPaymentStatus.Fail);

        trackPaymentTimeout(analyticsOfferStatus);
      }, 300000); // 5 минут

      checkPayment(paymentId)();
    };

    const getState = () => paymentStatus;

    const resetState = () => {
      isPaymentProcessed = undefined;
    };

    // Данный переключатель состояния оплаты внедрён для исключения ситуации когда модалка с фреймом оплаты открывается,
    // но оплата не производится. При этом запускается ненужный механизм проверки оплаченности транзакции, которой не было.
    const setPaymentProcessed = (state: boolean | undefined) => {
      // Стартовое значение undefined.
      switch (true) {
        case typeof state !== 'undefined':
          isPaymentProcessed = state;
          break;

        case typeof isPaymentProcessed === 'undefined':
          // При первой загрузке устанавливаем false.
          isPaymentProcessed = false;
          // Это удобно, так как в runCheckPaymentState (см. выше) мы проверяем переменную на истинность.
          // Это логично, так как первый шаг — это ввод данных платёжной карты. Без них нет транзакции.
          break;

        case isPaymentProcessed === false:
          // На следующем шаге мы устанавливаем true.
          isPaymentProcessed = true;
          // Транзакция началась, есть смысл проверять.
          break;
      }
    };

    const setPaymentSum = (value: number) => {
      paymentSum = value;
    };

    const getPaymentSum = () => paymentSum;

    $rootScope.$on('terms.changed', () => {
      resetState();
    });

    listenEvent(RETRY_DEPOSIT_ACCOUNT_PAYMENT, () => {
      trackRetryPaymentClick({ analyticsOfferStatus, amount: paymentSum });

      openModal('payByCard', { options: { source: 'balance', amount: paymentSum } });
    });

    return {
      runCheckPaymentState,
      getState,
      resetState,
      setPaymentProcessed,
      getPaymentSum,
      setPaymentSum,
    };
  },
];
